(() => {
  class SearchMenu extends HTMLElement {
    constructor() {
      super();

      // settings
      this.activeClasses = ['search-menu--active', 'suggestions--active'];
      this.activeClassesRemove = ['search-menu--active', 'suggestions--active'];
      this.desktopBp = window.matchMedia('(min-width: 1024px)');
      this.fetchParams = 'resources[limit]=10&resources[limit_scope]=each&resources[options][fields]=title,product_type,variants.title,variants.sku,vendor,body,tag';
      this.inputTimer = null;
      this.isSearchDesktopExpanded = this.hasAttribute('data-search-desktop-expanded');
      this.selector = 'search-menu';
      this.verticalScrollPosition = null;

      // els
      this.backdropEl = this.querySelector('[data-search-menu-backdrop]');
      this.closeEl = document.querySelectorAll('[data-search-menu-close]');
      this.inputEl = this.querySelector('[data-search-menu-query]');
      this.predictiveEl = this.querySelector('[data-predictive-search]');
      this.statusEl = this.querySelector('[data-search-menu-status]');

      // cache default content to show when results are empty
      this.cachedPredictiveDefaultHtml = this.predictiveEl ? this.predictiveEl.innerHTML : '';
    }

    connectedCallback() {
      this.listeners();
    }

    close() {
      window.searchMenuOpen = false;
      document.documentElement.classList.remove(...this.activeClassesRemove);

      requestAnimationFrame(() => {
        const focusEl = this.openEl ? this.openEl : null
        window.removeTrapFocus(focusEl);
        window.menuToggling = false;
      });

      this.lockScrollDisable();
    }

    lazyLoadSwipers() {
      window.loadSwiper();
    }

    lockScrollEnable() {
      window.lockScrollEnable();
    }

    lockScrollDisable() {
      window.lockScrollDisable();
    }

    listeners() {
      window.subscribe(PUB_SUB_EVENTS.searchToggle, event => {
        this.openEl = event.el;
        window.loadSwiper();
        this.lockScrollEnable();

        // move focus into the input so keyboard users land inside the menu
        requestAnimationFrame(() => this.inputEl.focus());
      });

      // close search with backdrop
      if (this.backdropEl) this.backdropEl.addEventListener('click', e => {
        this.close();
      });

      // close search with X
      this.closeEl.forEach(el => {
        el.addEventListener('click', e => {
          this.close();
        });
      });

      // activate suggestions when input is focused, via mouse or keyboard
      this.inputEl.addEventListener('focus', e => {
        this.onFocus(e);
      });

      // activate/update suggestions when typing in input
      this.inputEl.addEventListener('input', e => {
        window.debounce(this.debouceInput(e), 250);
      });

      // close with esc on keyboard
      window.addEventListener('keyup', evt => evt.code === 'Escape' && document.documentElement.classList.contains(this.activeClasses[0]) && this.close());
    }

    debouceInput(e) {
      this.onInput(e);
    }

    onFocus(e) {
      window.menuToggling = true;
      window.searchMenuOpen = true;

      this.lazyLoadSwipers();

      if (e.target.value !== '') {
        requestAnimationFrame(() => {
          this.onInput(e);
        });
      }

      document.documentElement.dataset.scrollDirection = 'down';
      document.documentElement.classList.add(...this.activeClasses);

      window.trapFocus({
        container: this,
        elementToFocus: this.inputEl,
      });

      document.querySelector('[data-product-sticky]')?.classList.remove('active');
      this.lockScrollEnable();
    }

    refreshTrapFocus() {
      if (!window.searchMenuOpen) return;
      window.trapFocus({ container: this, elementToFocus: document.activeElement });
    }

    // announce result count to screen reader users via the live region
    announceResultsCount(parsedProductEl) {
      if (!this.statusEl || !parsedProductEl) return;
      const count = parseInt(parsedProductEl.dataset.resultsCount, 10) || 0;
      this.statusEl.textContent = count > 0 ? `${count} result${count === 1 ? '' : 's'} available` : 'No results available';
    }

    onInput(e) {
      clearTimeout(this.inputTimer);

      this.inputTimer = setTimeout(() => {
        if (e.target.value.length < 1) {
          if (this.predictiveEl) this.predictiveEl.innerHTML = this.cachedPredictiveDefaultHtml;

          if (this.querySelector('[data-predictive-search-default-swiper].swiper-initialized')) {
            this.querySelector('[data-predictive-search-default-swiper]').classList.remove('swiper-initialized')
          }

          if (this.querySelector('[data-predictive-search-no-results]')) {
            this.querySelector('[data-predictive-search-no-results]').style.display = 'none'
          }

          if (this.statusEl) this.statusEl.textContent = '';

          requestAnimationFrame(() => {
            window.loadSwiper();
          });

          this.refreshTrapFocus();
        }

        /* fetch products using section rendering api so results match SRLP */
        if (e.target.value.length < 1) return

        fetch(`${Shopify.routes.root}search/?q=${e.target.value}&${this.fetchParams}&section_id=predictive-search-products`)
          .then((response) => {
            this.requestResponse = response;
            return response.text();
          })
          .then((text) => {
            if (!this.requestResponse.ok) {
              throw new Error(`${this.requestResponse.status}: ${text}`);
            }
            const productEl = this.querySelector('[data-search-menu-products]')
            const domParsed = new DOMParser().parseFromString(text, 'text/html');
            const parsedProductEl = domParsed.querySelector('[data-search-menu-products]');

            if (parsedProductEl && parsedProductEl.innerHTML !== '') {
              productEl.innerHTML = parsedProductEl.innerHTML
            }

            this.announceResultsCount(parsedProductEl);

            requestAnimationFrame(() => {
              window.loadSwiper();
            });

            this.refreshTrapFocus();
          })
          .catch((error) => {
            console.error(error);
          });

        /* fetch non-product suggestions using suggest api */
        fetch(`${Shopify.routes.root}search/suggest?q=${e.target.value}&${this.fetchParams}&section_id=predictive-search-content`)
          .then((response) => {
            this.requestResponse = response;
            return response.text();
          })
          .then((text) => {
            if (!this.requestResponse.ok) {
              throw new Error(`${this.requestResponse.status}: ${text}`);
            }

            const contentEl = this.querySelector('[data-search-menu-content]');
            const domParsed = new DOMParser().parseFromString(text, 'text/html');
            const parsedContentEl = domParsed.querySelector('[data-search-menu-content]');
            contentEl.innerHTML = parsedContentEl.innerHTML

            this.refreshTrapFocus();
          })
          .catch((error) => {
            console.error(error);
          });
      }, 250);
    }
  }

  class SearchToggle extends HTMLElement {
    constructor() {
      super();
      this.activeClasses = ['search-menu--active', 'suggestions--active'];
      this.desktopSource = 'search-toggle';
      this.el = this.querySelector('[data-search-menu-toggle]');
      this.menuSelector = 'search-menu';
    }

    connectedCallback() {
      this.hasSearchMenu = document.querySelector(this.menuSelector) !== null;
      this.trapFocusContainer = document.querySelector(this.menuSelector);
      this.trapFocusEl = document.querySelector(`${this.menuSelector} [data-search-menu-query]`);
      this.listeners();
    }

    listeners() {
      this.el.addEventListener('click', e => {
        if (!this.hasSearchMenu) return;
        e.preventDefault();

        window.menuToggling = true;
        window.searchMenuOpen = true;

        document.documentElement.classList.add(...this.activeClasses);
        document.querySelector('[data-product-sticky]')?.classList.remove('active');

        window.publish(PUB_SUB_EVENTS.searchToggle, {
          el: this.el,
          source: this.desktopSource,
        });
      });
    }
  }

  if (!customElements.get('search-menu')) {
    customElements.define('search-menu', SearchMenu);
  }

  if (!customElements.get('search-toggle')) {
    customElements.define('search-toggle', SearchToggle);
  }
})();
